// src/modules/ml/ClassificationLab.jsx
// Classification Lab — standalone ML sidebar module
// k-NN · Logistic Regression · Naive Bayes · Decision Tree · Confusion Matrix · ROC · Compare

import React, { useState, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import {
  parseCSVForML, inferTypes, kNN, logisticRegression, naiveBayes, decisionTree,
  confusionMatrix, trainTestSplit, rocCurve, auc, generateMockDataset, parseNum,
  randomForestClassifier, svmClassifier, baggingClassifier, adaBoostClassifier,
} from '../../utils/mlProcessing.js';
import { Panel, Metric, Tag, InfoBox, LogPanel, timestamp } from '../../components/ui.jsx';

const P=['var(--violet)','var(--cyan)','var(--green)','var(--gold)','var(--red)','var(--orange)','#f472b6','#34d399'];
const Sel=({value,onChange,options,label,style})=>(<div style={style}>{label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>}<select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'7px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>{options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}</select></div>);
const Btn=({onClick,children,color='var(--violet)',disabled,style})=>(<button onClick={onClick} disabled={disabled} style={{padding:'9px 18px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>);
const TABS=['upload','setup','knn','logistic','naivebayes','dtree','randomforest','svm','ensemble','compare'];

function ConfMatPanel({result,title,color}){
  const classes=result.classMetrics.map(m=>m.cls);
  return(
    <div style={{display:'grid',gridTemplateColumns:classes.length>3?'1fr':'auto 1fr',gap:'12px'}}>
      <Panel title={`CONFUSION MATRIX — ${title}`} accent={color}>
        <div style={{marginBottom:'6px',fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em'}}>Rows = actual · Cols = predicted</div>
        <div style={{display:'inline-block'}}>
          <div style={{display:'flex',gap:'2px',marginLeft:'80px',marginBottom:'3px'}}>
            {classes.map(c=><div key={c} style={{width:classes.length>4?'50px':'64px',textAlign:'center',fontSize:'9px',color:color,fontWeight:600}}>{c}</div>)}
          </div>
          {classes.map(actual=>(
            <div key={actual} style={{display:'flex',gap:'2px',alignItems:'center',marginBottom:'2px'}}>
              <div style={{width:'78px',textAlign:'right',paddingRight:'6px',fontSize:'9px',color:color,fontWeight:600}}>{actual}</div>
              {classes.map(predicted=>{
                const v=result.mat[actual]?.[predicted]||0,isTP=actual===predicted;
                return <div key={predicted} style={{width:classes.length>4?'50px':'64px',height:classes.length>4?'36px':'48px',borderRadius:'2px',background:isTP?color+'40':v>0?'var(--red)20':'var(--raised)',border:`1px solid ${isTP?color+'60':'var(--b0)'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'16px',fontWeight:700,color:isTP?color:v>0?'var(--red)':'var(--t3)'}}>{v}</div>;
              })}
            </div>
          ))}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginTop:'10px'}}>
          <Metric label="Accuracy" value={`${(result.accuracy*100).toFixed(1)}%`} color={color}/>
          <Metric label="Macro F1" value={result.macroF1.toFixed(3)} color={color}/>
        </div>
      </Panel>
      <Panel title="PER-CLASS METRICS" accent={color}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
          <thead><tr style={{background:'var(--deep)'}}>
            {['Class','Precision','Recall','F1','TP','FP','FN'].map(h=><th key={h} style={{padding:'5px 8px',textAlign:'left',color:'var(--t3)',fontSize:'9px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
          </tr></thead>
          <tbody>{result.classMetrics.map((m,i)=>(
            <tr key={m.cls} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.08)'}}>
              <td style={{padding:'5px 8px',color,fontWeight:600}}>{m.cls}</td>
              <td style={{padding:'5px 8px',color:'var(--t2)'}}>{(m.precision*100).toFixed(1)}%</td>
              <td style={{padding:'5px 8px',color:'var(--t2)'}}>{(m.recall*100).toFixed(1)}%</td>
              <td style={{padding:'5px 8px',color:m.f1>0.7?'var(--green)':m.f1>0.5?'var(--gold)':'var(--red)',fontWeight:600}}>{m.f1.toFixed(3)}</td>
              <td style={{padding:'5px 8px',color:'var(--green)'}}>{m.tp}</td>
              <td style={{padding:'5px 8px',color:'var(--red)'}}>{m.fp}</td>
              <td style={{padding:'5px 8px',color:'var(--orange)'}}>{m.fn}</td>
            </tr>
          ))}</tbody>
        </table>
      </Panel>
    </div>
  );
}

function TreeNode({node,depth=0}){
  if(!node)return null;
  if(node.leaf)return(<div style={{display:'flex',alignItems:'center',gap:'8px',marginLeft:`${depth*22}px`,marginBottom:'4px'}}>
    <div style={{width:'10px',height:'2px',background:'var(--b1)'}}/>
    <div style={{padding:'5px 10px',background:'var(--ggreen)',border:'1px solid var(--green)44',borderRadius:'3px',fontSize:'11px',color:'var(--green)',whiteSpace:'nowrap'}}>▶ {node.prediction} <span style={{color:'var(--t3)',fontSize:'9px'}}>(n={node.n})</span></div>
  </div>);
  return(<div style={{marginLeft:`${depth*22}px`,marginBottom:'2px'}}>
    <div style={{padding:'5px 10px',background:'var(--gcyan)',border:'1px solid var(--cyan)44',borderRadius:'3px',display:'inline-block',fontSize:'11px',color:'var(--cyan)',marginBottom:'4px'}}>
      <span style={{fontWeight:700}}>{node.col}</span> ≤ {node.thresh} <span style={{color:'var(--t3)',fontSize:'9px'}}>gain={node.gain} n={node.n}</span>
    </div>
    {depth<4&&(<>
      <div style={{marginLeft:'18px',borderLeft:'1px dashed var(--b1)',paddingLeft:'10px'}}>
        <div style={{fontSize:'8px',color:'var(--t3)',marginBottom:'2px'}}>TRUE ↓</div>
        <TreeNode node={node.left} depth={0}/>
      </div>
      <div style={{marginLeft:'18px',borderLeft:'1px dashed var(--b1)',paddingLeft:'10px',marginTop:'4px'}}>
        <div style={{fontSize:'8px',color:'var(--t3)',marginBottom:'2px'}}>FALSE ↓</div>
        <TreeNode node={node.right} depth={0}/>
      </div>
    </>)}
  </div>);
}

export default function ClassificationLab(){
  const [tab,setTab]=useState('upload');
  const [data,setData]=useState(null);
  const [logs,setLogs]=useState([{ts:'00:00:00',msg:'Classification Lab — upload data or choose a sample.',t:'info'}]);
  const [featureCols,setFeatureCols]=useState([]);
  const [labelCol,setLabelCol]=useState('');
  const [k,setK]=useState(5);
  const [testRatio,setTestRatio]=useState(0.2);
  const [knnResult,setKnnResult]=useState(null);
  const [logRegResult,setLogRegResult]=useState(null);
  const [nbResult,setNbResult]=useState(null);
  const [dtResult,setDtResult]=useState(null);
  const [rfResult,setRfResult]=useState(null);
  const [svmResult,setSvmResult]=useState(null);
  const [baggingResult,setBaggingResult]=useState(null);
  const [adaResult,setAdaResult]=useState(null);
  const [running,setRunning]=useState(false);
  const fileRef=useRef();
  const log=(msg,t='info')=>setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const loadData=(text,name,direct)=>{
    const parsed=direct||(text?parseCSVForML(text):null);
    if(!parsed){log('Parse error','err');return;}
    const types=inferTypes(parsed.headers,parsed.rows);
    const nc=types.filter(t=>t.type==='numeric').map(t=>t.col);
    const cc=types.filter(t=>t.type==='categorical').map(t=>t.col);
    setData({...parsed,types});
    setFeatureCols(nc.slice(0,4));
    setLabelCol(cc[0]||parsed.headers[parsed.headers.length-1]);
    log(`✓ "${name}": ${parsed.rows.length}×${parsed.headers.length} | classes: ${[...new Set(parsed.rows.map(r=>r[cc[0]||parsed.headers[parsed.headers.length-1]]).filter(Boolean))].join(', ')}`,'ok');
    setTab('setup');
  };

  const rows=data?.rows||[],types=data?.types||[];
  const nc=types.filter(t=>t.type==='numeric').map(t=>t.col);
  const cc=types.filter(t=>t.type==='categorical').map(t=>t.col);
  const classes=[...new Set(rows.map(r=>r[labelCol]).filter(Boolean))].sort();
  const classDist=classes.map(c=>({cls:c,count:rows.filter(r=>r[labelCol]===c).length,pct:+(rows.filter(r=>r[labelCol]===c).length/rows.length*100).toFixed(1)}));

  const runAsync=async(label,fn)=>{setRunning(true);log(`Running ${label}...`);await new Promise(r=>setTimeout(r,30));fn();setRunning(false);};

  const allResults=[
    knnResult&&{name:`k-NN (k=${k})`,acc:knnResult.cm.accuracy,f1:knnResult.cm.macroF1,color:'var(--cyan)'},
    logRegResult&&{name:'Logistic Reg',acc:logRegResult.cm.accuracy,f1:logRegResult.cm.macroF1,color:'var(--green)'},
    nbResult&&{name:'Naive Bayes',acc:nbResult.cm.accuracy,f1:nbResult.cm.macroF1,color:'var(--gold)'},
    dtResult&&{name:'Decision Tree',acc:dtResult.cm.accuracy,f1:dtResult.cm.macroF1,color:'var(--orange)'},
    rfResult&&{name:'Random Forest',acc:rfResult.cm.accuracy,f1:rfResult.cm.macroF1,color:'#34d399'},
    svmResult&&{name:'SVM (Linear)',acc:svmResult.cm.accuracy,f1:svmResult.cm.macroF1,color:'var(--red)'},
    baggingResult&&{name:'Bagging',acc:baggingResult.cm.accuracy,f1:baggingResult.cm.macroF1,color:'var(--violet)'},
    adaResult&&{name:'AdaBoost',acc:adaResult.cm.accuracy,f1:adaResult.cm.macroF1,color:'#f472b6'},
  ].filter(Boolean);

  return(
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?'var(--violet)':'transparent'}`,background:tab===t?'var(--gviolet)':'transparent',color:tab===t?'var(--violet)':'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* UPLOAD */}
      {tab==='upload'&&(
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
          <div onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){const r=new FileReader();r.onload=ev=>loadData(ev.target.result,f.name);r.readAsText(f);}}} onDragOver={e=>e.preventDefault()} onClick={()=>fileRef.current?.click()} style={{border:'2px dashed var(--b1)',borderRadius:'4px',padding:'52px 24px',textAlign:'center',cursor:'pointer',background:'var(--surface)'}}>
            <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={e=>{const f=e.target.files[0];if(f){const r=new FileReader();r.onload=ev=>loadData(ev.target.result,f.name);r.readAsText(f);}}}/>
            <div style={{fontSize:'40px',opacity:.3,marginBottom:'14px'}}>◐</div>
            <div style={{fontFamily:'Syne',fontWeight:700,fontSize:'18px',marginBottom:'7px'}}>Drop CSV File</div>
            <div style={{fontSize:'12px',color:'var(--t3)'}}>Needs numeric features + categorical target</div>
          </div>
          <Panel title="SAMPLE DATASETS" accent="var(--violet)">
            {[['Binary Classification','classification','x1,x2,x3 → class A/B'],['Multiclass (3)','multiclass','x1,x2,x3,x4 → Alpha/Beta/Gamma']].map(([name,type,desc])=>(
              <div key={type} onClick={()=>loadData(null,name,generateMockDataset(type))} style={{display:'flex',alignItems:'center',gap:'10px',padding:'10px',border:'1px solid var(--b0)',borderRadius:'3px',marginBottom:'6px',cursor:'pointer',background:'var(--raised)',transition:'all .12s'}} onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--violet)'}} onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--b0)'}}>
                <span style={{fontSize:'18px',color:'var(--violet)'}}>◐</span>
                <div style={{flex:1}}><div style={{fontSize:'12px',fontWeight:600,color:'var(--t1)'}}>{name}</div><div style={{fontSize:'10px',color:'var(--t3)'}}>{desc}</div></div>
                <span style={{color:'var(--violet)'}}>→</span>
              </div>
            ))}
            <LogPanel logs={logs} height={70}/>
          </Panel>
        </div>
      )}

      {/* SETUP */}
      {tab==='setup'&&data&&(
        <Panel title="DATASET SETUP & CLASS DISTRIBUTION" accent="var(--violet)">
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
            <div>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>FEATURE COLUMNS (select)</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'14px'}}>
                {nc.map(col=>(
                  <button key={col} onClick={()=>setFeatureCols(s=>s.includes(col)?s.filter(c=>c!==col):[...s,col])} style={{padding:'5px 12px',border:`1px solid ${featureCols.includes(col)?'var(--violet)':'var(--b0)'}`,background:featureCols.includes(col)?'var(--gviolet)':'var(--raised)',color:featureCols.includes(col)?'var(--violet)':'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>
                    {col}
                  </button>
                ))}
              </div>
              <Sel value={labelCol} onChange={setLabelCol} options={[...cc,...nc]} label="TARGET LABEL COLUMN"/>
              <div style={{marginTop:'10px'}}>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px'}}>TEST SET SIZE: {(testRatio*100).toFixed(0)}%</div>
                <input type="range" min={0.1} max={0.4} step={0.05} value={testRatio} onChange={e=>setTestRatio(parseFloat(e.target.value))} style={{width:'100%',accentColor:'var(--violet)'}}/>
              </div>
            </div>
            <div>
              <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>CLASS DISTRIBUTION</div>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={classDist}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                  <XAxis dataKey="cls" stroke="var(--t3)" tick={{fontSize:9}}/>
                  <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                  <Tooltip/>
                  <Bar dataKey="count" radius={[2,2,0,0]}>{classDist.map((_,i)=><Cell key={i} fill={P[i%P.length]} opacity={.85}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
              {classDist.length>0&&(
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginTop:'8px'}}>
                  {classDist.map((d,i)=><div key={d.cls} style={{padding:'4px 10px',background:P[i%P.length]+'18',border:`1px solid ${P[i%P.length]}44`,borderRadius:'3px',fontSize:'10px'}}><span style={{color:P[i%P.length],fontWeight:700}}>{d.cls}</span><span style={{color:'var(--t3)',marginLeft:'6px'}}>{d.count} ({d.pct}%)</span></div>)}
                </div>
              )}
              {(() => {
                const maxP=Math.max(...classDist.map(d=>d.pct)),minP=Math.min(...classDist.map(d=>d.pct));
                const imbal=classDist.length>1&&maxP/minP>2;
                return imbal&&<InfoBox color="var(--orange)" icon="⚠" style={{marginTop:'8px'}}>Class imbalance detected ({maxP.toFixed(0)}% vs {minP.toFixed(0)}%). Consider oversampling (SMOTE) or class weights. Accuracy can be misleading — use F1 score.</InfoBox>;
              })()}
            </div>
          </div>
        </Panel>
      )}

      {/* k-NN */}
      {tab==='knn'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="k-NEAREST NEIGHBORS CLASSIFIER" accent="var(--cyan)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Classifies by voting among the k nearest training points (Euclidean distance).
              No training phase — all computation at prediction time. k controls the bias-variance tradeoff.
            </div>
            <div style={{display:'flex',gap:'16px',alignItems:'flex-end',flexWrap:'wrap'}}>
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'5px'}}>k = {k} neighbors (odd preferred)</div>
                <input type="range" min={1} max={21} step={2} value={k} onChange={e=>setK(parseInt(e.target.value))} style={{width:'200px',accentColor:'var(--cyan)'}}/>
              </div>
              <Btn onClick={()=>runAsync('k-NN',()=>{
                const {train,test}=trainTestSplit(rows,testRatio);
                const preds=kNN(train,test,featureCols,labelCol,k).filter(p=>p.predicted!==null);
                const cm=confusionMatrix(preds,classes);
                setKnnResult({preds,cm,k,testSize:test.length});
                log(`k-NN k=${k}: acc=${cm.accuracy} macroF1=${cm.macroF1} | ${test.length} test samples`,'ok');
              })} color="var(--cyan)" disabled={running}>{running?'CLASSIFYING...':'RUN k-NN →'}</Btn>
            </div>
            <div style={{marginTop:'10px',padding:'10px',background:'var(--raised)',borderRadius:'3px',fontSize:'11px',color:'var(--t3)'}}>
              <strong style={{color:'var(--cyan)'}}>k=1:</strong> Low bias, high variance (memorizes training data). <strong style={{color:'var(--cyan)'}}>k large:</strong> High bias, low variance (smoother boundary). Best k is usually between 3–15 via cross-validation.
            </div>
          </Panel>
          {knnResult&&<ConfMatPanel result={knnResult.cm} title={`k-NN (k=${knnResult.k}, n=${knnResult.testSize} test)`} color="var(--cyan)"/>}
        </div>
      )}

      {/* LOGISTIC */}
      {tab==='logistic'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="LOGISTIC REGRESSION — Binary Classifier" accent="var(--green)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Learns P(y=1|x) = σ(w·x+b) where σ is the sigmoid function. Optimized via gradient descent on binary cross-entropy.
              Binary only. For multiclass, use Decision Tree or extend via one-vs-rest.
            </div>
            <Btn onClick={()=>runAsync('Logistic Regression',()=>{
              if(classes.length!==2){log(`Logistic Regression requires exactly 2 classes. Found: ${classes.join(', ')}`,'err');return;}
              const r=logisticRegression(rows,featureCols,labelCol,{lr:0.1,epochs:500});
              if(!r){log('Failed — need more data','err');return;}
              const preds=r.predictions.map(p=>({...p,predicted:p.predicted===1?classes[1]:classes[0],actual:p.actual===1?classes[1]:classes[0],correct:p.predicted===p.actual}));
              const cm=confusionMatrix(preds,classes);
              setLogRegResult({...r,cm,roc:rocCurve(r.predictions),classes,aucScore:auc(rocCurve(r.predictions))});
              log(`LogReg: acc=${cm.accuracy} macroF1=${cm.macroF1} AUC=${auc(rocCurve(r.predictions))}`,'ok');
            })} color="var(--green)" disabled={running}>{running?'TRAINING...':'TRAIN →'}</Btn>
          </Panel>
          {logRegResult&&(
            <>
              <ConfMatPanel result={logRegResult.cm} title="Logistic Regression" color="var(--green)"/>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
                <Panel title="TRAINING LOSS (Cross-Entropy)" accent="var(--green)">
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={logRegResult.lossHistory}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="epoch" stroke="var(--t3)" tick={{fontSize:9}}/>
                      <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                      <Tooltip/>
                      <Line type="monotone" dataKey="loss" stroke="var(--green)" strokeWidth={2} dot={false}/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
                <Panel title="ROC CURVE" accent="var(--violet)" badge={`AUC = ${logRegResult.aucScore}`}>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={logRegResult.roc}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="fpr" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'FPR (1-Specificity)',position:'insideBottom',offset:-5,fill:'var(--t3)',fontSize:9}}/>
                      <YAxis dataKey="tpr" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'TPR (Recall)',angle:-90,position:'insideLeft',fill:'var(--t3)',fontSize:9}}/>
                      <Tooltip/>
                      <ReferenceLine segment={[{x:0,y:0},{x:1,y:1}]} stroke="var(--b1)" strokeDasharray="5 3" label={{value:'Random',fill:'var(--t3)',fontSize:8}}/>
                      <Line type="monotone" dataKey="tpr" stroke="var(--violet)" strokeWidth={2.5} dot={false} name="ROC Curve"/>
                    </LineChart>
                  </ResponsiveContainer>
                  <InfoBox color="var(--t3)" icon="◈">AUC=0.5 → random. AUC=1.0 → perfect. AUC {'>'} 0.9 → excellent. Threshold-agnostic performance measure.</InfoBox>
                </Panel>
              </div>
            </>
          )}
        </div>
      )}

      {/* NAIVE BAYES */}
      {tab==='naivebayes'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="GAUSSIAN NAIVE BAYES" accent="var(--gold)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Bayes' theorem with conditional independence assumption: P(y|x) ∝ P(y)·Π P(xᵢ|y).
              Each feature modeled as Gaussian: P(xᵢ|y) = N(μᵢᵧ, σᵢᵧ). Parameters estimated via MLE.
              Fast, interpretable, works well with small data.
            </div>
            <Btn onClick={()=>runAsync('Naive Bayes',()=>{
              const {train,test}=trainTestSplit(rows,testRatio);
              const r=naiveBayes(train,test,featureCols,labelCol);
              const cm=confusionMatrix(r.predictions.filter(p=>p.predicted),classes);
              setNbResult({...r,cm});
              log(`Naive Bayes: acc=${cm.accuracy} macroF1=${cm.macroF1}`,'ok');
            })} color="var(--gold)" disabled={running}>{running?'RUNNING...':'RUN NAIVE BAYES →'}</Btn>
          </Panel>
          {nbResult&&(
            <>
              <ConfMatPanel result={nbResult.cm} title="Gaussian Naive Bayes" color="var(--gold)"/>
              <Panel title="CLASS STATISTICS — Learned Gaussian Parameters" accent="var(--gold)">
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:'10px'}}>
                  {Object.entries(nbResult.stats).map(([cls,s],ci)=>(
                    <div key={cls} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${P[ci%P.length]}33`,borderRadius:'3px'}}>
                      <div style={{fontWeight:700,color:P[ci%P.length],marginBottom:'4px',fontSize:'12px'}}>Class: {cls}</div>
                      <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'6px'}}>Prior: {(s.prior*100).toFixed(1)}%</div>
                      {featureCols.slice(0,5).map((col,fi)=>(
                        <div key={col} style={{fontSize:'10px',display:'flex',justifyContent:'space-between',borderBottom:'1px solid var(--b0)',padding:'3px 0'}}>
                          <span style={{color:'var(--t2)'}}>{col}</span>
                          <span style={{color:P[ci%P.length],fontFamily:'JetBrains Mono'}}>μ={s.features[fi]?.mean.toFixed(2)} σ={s.features[fi]?.std.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <InfoBox color="var(--t3)" icon="◈" style={{marginTop:'10px'}}>
                  If μ values differ significantly between classes for a feature, that feature is discriminative. 
                  Overlapping Gaussians = low separability for that feature.
                </InfoBox>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* DECISION TREE */}
      {tab==='dtree'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="DECISION TREE — CART with Gini Impurity" accent="var(--orange)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Classification and Regression Trees (CART) greedily selects the split that maximizes Gini information gain.
              Gini impurity G = 1 − Σpᵢ². Max depth=5 prevents overfitting. Tree is interpretable — each node is a readable rule.
            </div>
            <Btn onClick={()=>runAsync('Decision Tree',()=>{
              const r=decisionTree(rows,featureCols,labelCol,5);
              const cm=confusionMatrix(r.predictions,classes);
              setDtResult({...r,cm});
              log(`Decision Tree: train acc=${cm.accuracy} macroF1=${cm.macroF1} (NOTE: train accuracy, not holdout)`,'ok');
            })} color="var(--orange)" disabled={running}>{running?'BUILDING...':'BUILD TREE →'}</Btn>
          </Panel>
          {dtResult&&(
            <>
              <ConfMatPanel result={dtResult.cm} title="Decision Tree (CART, train eval)" color="var(--orange)"/>
              <Panel title="TREE STRUCTURE — Decision Rules" accent="var(--orange)">
                <div style={{marginBottom:'8px',fontSize:'11px',color:'var(--t3)'}}>
                  Each node: feature ≤ threshold. Left branch = TRUE. Right = FALSE. Green nodes = leaf predictions.
                  Showing up to depth 4.
                </div>
                <div style={{overflowX:'auto',maxHeight:'400px',overflowY:'auto'}}>
                  <TreeNode node={dtResult.tree} depth={0}/>
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* RANDOM FOREST */}
      {tab==='randomforest'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="RANDOM FOREST CLASSIFIER" accent="#34d399">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Ensemble of 20 decision trees trained on bootstrap samples with random feature subsets (√p features per split).
              Reduces overfitting dramatically vs a single tree. Majority vote across all trees for final prediction.
            </div>
            <Btn onClick={()=>runAsync('Random Forest',()=>{
              const {train,test}=trainTestSplit(rows,testRatio);
              const r=randomForestClassifier(train,test,featureCols,labelCol,{nTrees:20,maxDepth:5});
              if(!r){log('Random Forest failed — need numeric features','err');return;}
              const cm=confusionMatrix(r.predictions,classes);
              setRfResult({...r,cm});
              log(`Random Forest (20 trees): acc=${cm.accuracy} macroF1=${cm.macroF1}`,'ok');
            })} color="#34d399" disabled={running}>{running?'BUILDING FOREST...':'BUILD FOREST →'}</Btn>
          </Panel>
          {rfResult&&<ConfMatPanel result={rfResult.cm} title="Random Forest (20 trees)" color="#34d399"/>}
        </div>
      )}

      {/* SVM */}
      {tab==='svm'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="SUPPORT VECTOR MACHINE — Linear Kernel (Binary)" accent="var(--red)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              Linear SVM finds the maximum-margin hyperplane separating classes. Implemented via Pegasos SGD algorithm.
              Binary classification only (requires exactly 2 classes). Robust to outliers due to margin maximization.
            </div>
            {classes.length!==2&&<InfoBox color="var(--gold)" icon="⚠">SVM requires exactly 2 classes. Your dataset has {classes.length} classes ({classes.join(', ')}). Switch to a binary dataset.</InfoBox>}
            {classes.length===2&&(
              <Btn onClick={()=>runAsync('SVM',()=>{
                const {train,test}=trainTestSplit(rows,testRatio);
                const r=svmClassifier(train,test,featureCols,labelCol);
                if(!r){log('SVM failed','err');return;}
                const cm=confusionMatrix(r.predictions,classes);
                setSvmResult({...r,cm});
                log(`SVM (Linear): acc=${cm.accuracy} macroF1=${cm.macroF1}`,'ok');
              })} color="var(--red)" disabled={running}>{running?'TRAINING...':'TRAIN SVM →'}</Btn>
            )}
          </Panel>
          {svmResult&&<ConfMatPanel result={svmResult.cm} title="SVM (Linear Kernel)" color="var(--red)"/>}
        </div>
      )}

      {/* ENSEMBLE */}
      {tab==='ensemble'&&data&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="ENSEMBLE METHODS — Bagging & AdaBoost" accent="var(--violet)">
            <div style={{marginBottom:'10px',fontSize:'11px',color:'var(--t3)'}}>
              <strong style={{color:'var(--violet)'}}>Bagging (Bootstrap Aggregating):</strong> trains 15 independent stumps on bootstrap samples, reduces variance.&nbsp;
              <strong style={{color:'#f472b6'}}>AdaBoost:</strong> sequential boosting — each round focuses on misclassified points by adjusting weights. Binary only.
            </div>
            <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
              <Btn onClick={()=>runAsync('Bagging',()=>{
                const {train,test}=trainTestSplit(rows,testRatio);
                const r=baggingClassifier(train,test,featureCols,labelCol,{nEstimators:15});
                if(!r){log('Bagging failed','err');return;}
                const cm=confusionMatrix(r.predictions,classes);
                setBaggingResult({...r,cm});
                log(`Bagging (15 stumps): acc=${cm.accuracy} macroF1=${cm.macroF1}`,'ok');
              })} color="var(--violet)" disabled={running}>{running?'BAGGING...':'RUN BAGGING (15) →'}</Btn>
              <Btn onClick={()=>runAsync('AdaBoost',()=>{
                if(classes.length!==2){log('AdaBoost requires 2 classes','err');return;}
                const {train,test}=trainTestSplit(rows,testRatio);
                const r=adaBoostClassifier(train,test,featureCols,labelCol,{nEstimators:30});
                if(!r){log('AdaBoost failed','err');return;}
                const cm=confusionMatrix(r.predictions,classes);
                setAdaResult({...r,cm});
                log(`AdaBoost (30): acc=${cm.accuracy} macroF1=${cm.macroF1}`,'ok');
              })} color="#f472b6" disabled={running}>{running?'BOOSTING...':'RUN ADABOOST (30) →'}</Btn>
            </div>
          </Panel>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {baggingResult&&<ConfMatPanel result={baggingResult.cm} title="Bagging (15 stumps)" color="var(--violet)"/>}
            {adaResult&&<ConfMatPanel result={adaResult.cm} title="AdaBoost (30 rounds)" color="#f472b6"/>}
          </div>
          {!baggingResult&&!adaResult&&<InfoBox color="var(--t3)" icon="◐">Run one or both ensemble methods above to see results.</InfoBox>}
        </div>
      )}

      {/* COMPARE */}
      {tab==='compare'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="MODEL COMPARISON DASHBOARD" accent="var(--violet)">
            {data&&(
              <div style={{marginBottom:'12px'}}>
                <Btn onClick={()=>runAsync('All Classifiers',()=>{
                  const {train,test}=trainTestSplit(rows,testRatio);
                  try{const r=kNN(train,test,featureCols,labelCol,k).filter(p=>p.predicted!==null);const cm=confusionMatrix(r,classes);setKnnResult({preds:r,cm,k,testSize:test.length});}catch(e){}
                  try{const {train:tr2,test:te2}=trainTestSplit(rows,testRatio);const r=naiveBayes(tr2,te2,featureCols,labelCol);const cm=confusionMatrix(r.predictions.filter(p=>p.predicted),classes);setNbResult({...r,cm});}catch(e){}
                  try{const r=decisionTree(rows,featureCols,labelCol,5);const cm=confusionMatrix(r.predictions,classes);setDtResult({...r,cm});}catch(e){}
                  try{const {train:tr3,test:te3}=trainTestSplit(rows,testRatio);const r=randomForestClassifier(tr3,te3,featureCols,labelCol,{nTrees:20});if(r){const cm=confusionMatrix(r.predictions,classes);setRfResult({...r,cm});}}catch(e){}
                  try{const {train:tr4,test:te4}=trainTestSplit(rows,testRatio);const r=baggingClassifier(tr4,te4,featureCols,labelCol,{nEstimators:15});if(r){const cm=confusionMatrix(r.predictions,classes);setBaggingResult({...r,cm});}}catch(e){}
                  if(classes.length===2){
                    try{const r=logisticRegression(rows,featureCols,labelCol,{lr:0.1,epochs:500});if(r){const preds=r.predictions.map(p=>({...p,predicted:p.predicted===1?classes[1]:classes[0],actual:p.actual===1?classes[1]:classes[0],correct:p.predicted===p.actual}));const cm=confusionMatrix(preds,classes);setLogRegResult({...r,cm,roc:rocCurve(r.predictions),aucScore:auc(rocCurve(r.predictions))});}}catch(e){}
                    try{const {train:tr5,test:te5}=trainTestSplit(rows,testRatio);const r=svmClassifier(tr5,te5,featureCols,labelCol);if(r){const cm=confusionMatrix(r.predictions,classes);setSvmResult({...r,cm});}}catch(e){}
                    try{const {train:tr6,test:te6}=trainTestSplit(rows,testRatio);const r=adaBoostClassifier(tr6,te6,featureCols,labelCol,{nEstimators:30});if(r){const cm=confusionMatrix(r.predictions,classes);setAdaResult({...r,cm});}}catch(e){}
                  }
                  log('All classifiers trained!','ok');
                })} color="var(--violet)" disabled={running}>⚡ RUN ALL CLASSIFIERS →</Btn>
                <span style={{marginLeft:'10px',fontSize:'10px',color:'var(--t3)'}}>Binary-only models (LogReg, SVM, AdaBoost) skipped for multi-class datasets</span>
              </div>
            )}
            {allResults.length===0
              ?<InfoBox color="var(--t3)" icon="◐">Train models in other tabs first, or click RUN ALL above.</InfoBox>
              :(
                <>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'12px'}}>
                    <Panel title="ACCURACY COMPARISON">
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={[...allResults].sort((a,b)=>b.acc-a.acc)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                          <XAxis type="number" domain={[0,1]} stroke="var(--t3)" tick={{fontSize:9}}/>
                          <YAxis type="category" dataKey="name" stroke="var(--t3)" tick={{fontSize:9}} width={100}/>
                          <Tooltip formatter={v=>[`${(v*100).toFixed(1)}%`,'Accuracy']}/>
                          <Bar dataKey="acc" radius={[0,2,2,0]}>{[...allResults].sort((a,b)=>b.acc-a.acc).map(m=><Cell key={m.name} fill={m.color} opacity={.85}/>)}</Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Panel>
                    <Panel title="MACRO F1 SCORE">
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={[...allResults].sort((a,b)=>b.f1-a.f1)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                          <XAxis type="number" domain={[0,1]} stroke="var(--t3)" tick={{fontSize:9}}/>
                          <YAxis type="category" dataKey="name" stroke="var(--t3)" tick={{fontSize:9}} width={100}/>
                          <Tooltip formatter={v=>[`${(v*100).toFixed(1)}%`,'Macro F1']}/>
                          <Bar dataKey="f1" radius={[0,2,2,0]}>{[...allResults].sort((a,b)=>b.f1-a.f1).map(m=><Cell key={m.name} fill={m.color} opacity={.85}/>)}</Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Panel>
                  </div>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                    <thead><tr style={{background:'var(--deep)'}}>
                      {['Rank','Model','Accuracy','Macro F1','Status'].map(h=><th key={h} style={{padding:'8px 12px',textAlign:'left',color:'var(--t3)',fontSize:'10px',borderBottom:'1px solid var(--b0)'}}>{h}</th>)}
                    </tr></thead>
                    <tbody>{[...allResults].sort((a,b)=>b.f1-a.f1).map((m,i)=>(
                      <tr key={m.name} style={{borderBottom:'1px solid var(--b0)',background:i===0?m.color+'12':'transparent'}}>
                        <td style={{padding:'8px 12px',color:'var(--t3)',fontWeight:700}}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}</td>
                        <td style={{padding:'8px 12px',color:m.color,fontWeight:600}}>{m.name}</td>
                        <td style={{padding:'8px 12px',color:'var(--t1)',fontFamily:'JetBrains Mono'}}>{(m.acc*100).toFixed(1)}%</td>
                        <td style={{padding:'8px 12px',color:'var(--t1)',fontFamily:'JetBrains Mono'}}>{(m.f1*100).toFixed(1)}%</td>
                        <td style={{padding:'8px 12px'}}><Tag color={i===0?'var(--gold)':i===allResults.length-1?'var(--t3)':'var(--t3)'}>{i===0?'🥇 BEST':i===allResults.length-1?'⬟ LOWEST':'◈ MID'}</Tag></td>
                      </tr>
                    ))}</tbody>
                  </table>
                </>
              )
            }
          </Panel>
        </div>
      )}
      {!['upload'].includes(tab)&&<LogPanel logs={logs} height={55}/>}
    </div>
  );
}
